import { z } from 'zod'
import { supabase } from '../lib/supabase'

/** Stringa vuota = campo non compilato: nel database e' `null`, non ''. */
const opzionale = (formato: RegExp, messaggio: string) =>
  z
    .string()
    .trim()
    .transform((v) => (v === '' ? null : v))
    .refine((v) => v === null || formato.test(v), messaggio)

export const SchemaAzienda = z.object({
  ragioneSociale: z.string().trim().min(1, 'Ragione sociale obbligatoria'),
  /** Persona fisica (16) o giuridica (11 cifre): l'unicita' e' per partner. */
  codiceFiscale: z
    .string()
    .trim()
    .transform((v) => v.toUpperCase())
    .refine((v) => /^([A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]|\d{11})$/.test(v), 'Codice fiscale non valido'),
  partitaIva: opzionale(/^\d{11}$/, 'Partita IVA di 11 cifre'),
  telefono: opzionale(/^[\d +./()-]{4,}$/, 'Telefono non valido'),
  email: opzionale(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'Indirizzo email non valido'),
})

export type DatiAzienda = z.input<typeof SchemaAzienda>

export interface Azienda {
  readonly id: number
  readonly ragioneSociale: string
  readonly codiceFiscale: string
  readonly partitaIva: string | null
  readonly telefono: string | null
  readonly email: string | null
  readonly impianti: number
}

interface RigaAzienda {
  id: number
  ragione_sociale: string
  codice_fiscale: string
  partita_iva: string | null
  telefono: string | null
  email: string | null
  impianto: { count: number }[]
}

export async function elencaAziende(): Promise<Azienda[]> {
  const { data, error } = await supabase
    .from('azienda')
    .select('id, ragione_sociale, codice_fiscale, partita_iva, telefono, email, impianto(count)')
    .order('ragione_sociale')
  if (error) throw new Error(error.message)
  return ((data ?? []) as RigaAzienda[]).map((r) => ({
    id: r.id,
    ragioneSociale: r.ragione_sociale,
    codiceFiscale: r.codice_fiscale,
    partitaIva: r.partita_iva,
    telefono: r.telefono,
    email: r.email,
    impianti: r.impianto[0]?.count ?? 0,
  }))
}

export async function aggiornaAzienda(id: number, dati: DatiAzienda): Promise<void> {
  const d = SchemaAzienda.parse(dati)
  const { error } = await supabase
    .from('azienda')
    .update({
      ragione_sociale: d.ragioneSociale,
      codice_fiscale: d.codiceFiscale,
      partita_iva: d.partitaIva,
      telefono: d.telefono,
      email: d.email,
    })
    .eq('id', id)
  // azienda_cf_partner_idx: un codice fiscale per partner.
  if (error) throw new Error(error.code === '23505' ? 'Azienda gia registrata con questo codice fiscale' : error.message)
}
